import { initApolloClient } from '@drycut/apollo';
import { GetStoreDocument } from '@drycut/graphql';
import { MobileNavbar, Para, PromotionBadge, Stack } from '@drycut/ui-components';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { PassBubble } from '../PassBubble';
import { useCheckoutStore } from './checkoutStore';
import { Step1 } from './Step1';
import { Step2 } from './Step2';
import { Step3 } from './Step3';
import { Step4 } from './Step4';

type BookFormValues = {
  couponCode: string;
  existingCustomer: boolean;
  kidCustomer: boolean;
  name: string;
  phone: string;
  silentBooking: boolean;
};

export type BookingCheckoutProps = {
  hideIntroText?: boolean;
  hidePassBadge?: boolean;
  storeSlug?: string;
};

export function BookingCheckout({
  hidePassBadge = false,
  hideIntroText = false, // We will hide the itnro text for iframe checkouts
  storeSlug,
}: BookingCheckoutProps): React.ReactElement {
  const defaultValues: BookFormValues = {
    phone: '',
    name: '',
    existingCustomer: false,
    kidCustomer: false,
    silentBooking: false,
    couponCode: '', // legacy, we might remove this in the future to use the new zustand store value
  };

  const formMethods = useForm<BookFormValues>({ defaultValues, mode: 'onChange' });
  const selectedStore = useCheckoutStore((state) => state.selectedStore);

  // // Set the store slug if it is passed in as a prop
  useEffect(() => {
    if (!storeSlug) return;

    let cancelled = false;

    async function loadStore() {
      const client = initApolloClient();

      const { data } = await client.query({
        query: GetStoreDocument,
        variables: { slug: storeSlug ?? '' },
      });

      if (cancelled) return;

      const store = data?.store ?? null;

      useCheckoutStore.setState({
        selectedStore: store,
        selectedSchedule: undefined,
        scheduled: false,
      });
    }

    void loadStore();

    return () => {
      cancelled = true;
    };
  }, [storeSlug]);

  return (
    <FormProvider {...formMethods}>
      <MobileNavbar title="DRYCUT" />

      <Stack gap={10} className="relative">
        {!hideIntroText && (
          <Para className="max-w-prose text-center mx-auto" pretty>
            Hoi! Hier kannst Du Dich für Deinen Cut anmelden. Bitte denk daran, dass das Resultat
            dann am Besten wird, wenn Du mit sauberen Haaren zum Termin kommst. Wir freuen uns auf
            Dich!
          </Para>
        )}

        {!hidePassBadge && (
          <PassBubble storeSlug={storeSlug} className="-right-8 -top-20 lg:-right-14 lg:-top-14" />
        )}
        {selectedStore && (
          <PromotionBadge
            store={selectedStore}
            className="-top-20 -left-8 lg:-top-16 lg:-left-16"
          />
        )}

        <Stack gap={6} className="items-start flex-col lg:flex-row h-full gap-10 lg:gap-6">
          <Stack gap={4} className="lg:flex-1 w-full">
            <Step1 storeSlug={storeSlug} />
          </Stack>

          <Stack gap={4} className="lg:flex-1 w-full">
            <Step2 />
          </Stack>

          <Stack gap={4} className="lg:flex-1 w-full">
            <Step3 />
          </Stack>

          <Stack gap={4} className="lg:flex-1 w-full">
            <Step4 />
          </Stack>
        </Stack>
      </Stack>
    </FormProvider>
  );
}
